import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import {
  CreateContractorNote,
  DeleteContractorNote,
  FetchContractorNotes,
  UpdateContractorNote,
} from 'src/constants/actionTypes'
import { ContractorNote } from 'src/interfaces/note'

interface ContractorNotesPayload {
  contractorId: string
}

function* onFetchContractorNotes(
  action: AsyncAction<ContractorNotesPayload, Array<ContractorNote>>,
) {
  const { contractorId } = action.payload

  try {
    const {
      payload,
    }: {
      payload: Array<ContractorNote>
    } = yield call(callSecureApi, Endpoints.FetchContractorNotes, {
      apiService: ApiService.Paraworks,
      method: 'GET',
      params: [
        {
          value: contractorId,
          field: ':contractorId',
        },
      ],
    })

    yield put(FetchContractorNotes.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch contractor notes'
    yield put(FetchContractorNotes.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CreateContractorNotePayload {
  contractorId: string
  note: string
}

function* onCreateContractorNote(action: AsyncAction<CreateContractorNotePayload, ContractorNote>) {
  const { contractorId, note } = action.payload

  try {
    const { payload }: { payload: ContractorNote } = yield call(
      callSecureApi,
      Endpoints.CreateContractorNote,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: {
          note,
        },
        params: [
          {
            field: ':contractorId',
            value: contractorId,
          },
        ],
      },
    )

    yield put(CreateContractorNote.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to create note'

    yield put(CreateContractorNote.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateContractorNotePayload {
  contractorId: string
  noteId: string
  note: string
}

function* onUpdateContractorNote(action: AsyncAction<UpdateContractorNotePayload, ContractorNote>) {
  const { contractorId, noteId, note } = action.payload

  try {
    const { payload }: { payload: ContractorNote } = yield call(
      callSecureApi,
      Endpoints.UpdateContractorNote,
      {
        apiService: ApiService.Paraworks,
        method: 'PATCH',
        body: {
          note,
        },
        params: [
          {
            field: ':contractorId',
            value: contractorId,
          },
          {
            field: ':noteId',
            value: noteId,
          },
        ],
      },
    )

    yield put(UpdateContractorNote.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update note'

    yield put(UpdateContractorNote.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeleteContractorNotePayload {
  contractorId: string
  noteId: string
}

function* onDeleteContractorNote(action: AsyncAction<DeleteContractorNotePayload, void>) {
  const { contractorId, noteId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.DeleteContractorNote, {
      apiService: ApiService.Paraworks,
      returnResponse: true,
      method: 'DELETE',
      params: [
        {
          field: ':contractorId',
          value: contractorId,
        },
        {
          field: ':noteId',
          value: noteId,
        },
      ],
    })

    yield put(DeleteContractorNote.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to delete note'

    yield put(DeleteContractorNote.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* noteSaga() {
  yield takeEvery(FetchContractorNotes.type.REQUEST, onFetchContractorNotes)
  yield takeEvery(CreateContractorNote.type.REQUEST, onCreateContractorNote)
  yield takeEvery(UpdateContractorNote.type.REQUEST, onUpdateContractorNote)
  yield takeEvery(DeleteContractorNote.type.REQUEST, onDeleteContractorNote)
}
